import { computed, MaybeRef, Ref, unref } from "vue";
import { useRouter } from "vue-router";
import {
  FormResponseStatus,
  HousingApplication,
  HousingApplicationDto,
  HousingApplicationStatus,
  HousingSiteResultType,
  Workflow,
} from "~/api";
import { applicationRouteMap } from "~/router/mainRouter";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";
import { ApplicationWizardStep } from "../Intake/types/plusTypes";

export interface HousingApplicationStepStatus {
  completed: boolean;
  statusDisplayText: string;
}

type StepStatusMap = {
  [key in ApplicationWizardStep]: HousingApplicationStepStatus;
};
interface UseHousingApplicationWizardReturnValue {
  stepStatusMap: Ref<StepStatusMap>;
  completedSteps: Ref<ApplicationWizardStep[]>;
  disabledSteps: Ref<ApplicationWizardStep[]>;
  handleGotoStep: (step: ApplicationWizardStep) => void;
  handleSubmitApplication: () => Promise<HousingApplicationDto | void>;
}

/**
 * Composable for handling the computed statuses of a housing application in the wizard.
 */
export const useHousingAplicationWizard = (
  application: MaybeRef<HousingApplication | undefined>,
  workflow?: MaybeRef<Workflow | undefined>,
): UseHousingApplicationWizardReturnValue => {
  const selectLocationsStatus = computed<HousingApplicationStepStatus>(() => {
    const selectedLocationsCount =
      unref(application)?.siteResults?.filter(
        (site) => site.resultType === HousingSiteResultType.PREFERRED,
      ).length ?? 0;

    if (selectedLocationsCount === 0) {
      return {
        completed: false,
        statusDisplayText: "None Selected",
      };
    }
    return {
      completed: true,
      statusDisplayText: `${selectedLocationsCount} Selected`,
    };
  });

  const applicationFormStatus = computed<HousingApplicationStepStatus>(() => {
    // console.log(
    //   "Computing application form status with workflow:",
    //   unref(workflow),
    // );
    const wf = unref(workflow);
    if (wf) {
      const applicationForm = wf.formResponses?.find(
        (fr) =>
          fr.formDefinition?.formCode?.trim().toUpperCase() === "MAINFORM",
      );
      // If the workflow has started but we can't find the form response, treat it as in progress (since the form response should be created when the workflow starts)
      const status = applicationForm?.status ?? "";
      if (
        !status ||
        status === FormResponseStatus.NEW ||
        status === FormResponseStatus.DRAFT
      ) {
        const answerCount = applicationForm?.answers?.length ?? 0;
        return {
          completed: false,
          statusDisplayText: answerCount > 0 ? "In Progress" : "Not Started",
        };
      }
      return {
        completed: true,
        statusDisplayText: "Completed",
      };
    }

    // Fallback: derive status from form response when workflow is not available
    // TODO: Main Application form definition GUID — 420bda07-06c4-4772-8d45-28a277cec11d
    const applicationForm = unref(application)?.formResponses?.find(
      (fr) =>
        fr.formDefinitionGuid === "420bda07-06c4-4772-8d45-28a277cec11d" ||
        fr.formDefinitionGuid === "05a45396-8ef8-4023-8b63-0ab4737bf4ba",
    );

    if (applicationForm === undefined) {
      return {
        completed: false,
        statusDisplayText: "Loading...",
      };
    } else if (applicationForm.status === FormResponseStatus.NEW) {
      return {
        completed: false,
        statusDisplayText: "Not Started",
      };
    } else if (applicationForm.status === FormResponseStatus.DRAFT) {
      return {
        completed: false,
        statusDisplayText: "In Progress",
      };
    } else {
      return {
        completed: true,
        statusDisplayText: "Completed",
      };
    }
  });

  const reviewAndSubmitStatus = computed<HousingApplicationStepStatus>(() => {
    const completed =
      unref(application)?.status !== HousingApplicationStatus.DRAFT;

    return {
      completed,
      statusDisplayText: unref(application)?.status ?? "Draft",
    };
  });

  const stepStatusMap = computed<StepStatusMap>(() => ({
    [ApplicationWizardStep.SelectLocations]: selectLocationsStatus.value,
    [ApplicationWizardStep.ApplicationForm]: applicationFormStatus.value,
    [ApplicationWizardStep.ReviewAndSubmit]: reviewAndSubmitStatus.value,
  }));

  const completedSteps = computed<ApplicationWizardStep[]>(() => {
    const completedSteps: ApplicationWizardStep[] = Object.entries(
      stepStatusMap.value,
    )
      .filter(([_k, v]) => v.completed)
      .map(([k]) => parseInt(k) as ApplicationWizardStep);

    return completedSteps;
  });

  const disabledSteps = computed<ApplicationWizardStep[]>(() => {
    if (unref(application)?.status !== HousingApplicationStatus.DRAFT) {
      return [ApplicationWizardStep.SelectLocations];
    }
    return [];
  });

  const router = useRouter();
  function handleGotoStep(step: ApplicationWizardStep) {
    if (disabledSteps.value.includes(step)) {
      console.warn("User clicked on a step that was disabled:", step);
      return;
    }
    router.push({
      name: applicationRouteMap.Intake.children.Application.children
        .ApplicationWizard.name,
      params: {
        step: step,
        applicationGuid: unref(application)?.guid,
      },
    });
  }

  async function handleSubmitApplication() {
    const app = unref(application);
    if (app === undefined) {
      showError(
        "Developer error: tried to submit an application that was undefined",
      );
      return;
    } else if (
      !stepStatusMap.value[ApplicationWizardStep.SelectLocations].completed ||
      !stepStatusMap.value[ApplicationWizardStep.ApplicationForm].completed
    ) {
      showError(
        "Developer error: tried to submit an application that was not completed",
      );
      return;
    } else if (app.status !== HousingApplicationStatus.DRAFT) {
      showError(
        "Developer error: tried to submit an application that was already submitted",
      );
      return;
    }

    return await apiClient.housingApplication
      .submitHousingApplication(app.guid)
      .catch((error) => {
        showError(error);
      });
  }
  return {
    stepStatusMap,
    completedSteps,
    disabledSteps,
    handleGotoStep,
    handleSubmitApplication,
  };
};
